/**
 * MockDraftControls - [Hotfix Bundle v1.1 §4 HF-3]
 *
 * Renders Stop/Delete buttons for the commissioner of a Mock league while
 * the draft is in LOBBY, DRAFTING, or PAUSED state. Live leagues do not see
 * these controls - destructive mid-draft operations are admin-only for Live.
 *
 * Stop: pauses the draft (engine state preserved + DraftSession.state='PAUSED'
 *       persisted to DB per option (b) so cold-engine restart can resume),
 *       navigates back to the lobby.
 * Delete: hard-delete via DELETE /api/simulator/{leagueId} with type-the-name
 *         confirmation, redirects to dashboard.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Pause, Trash2, X } from "lucide-react";
import { authHeaders } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOCK_LEAGUE_TYPES = new Set([
  "MOCK_DRAFT_MOCK_SEASON",
  "MOCK_DRAFT_ONLY",
  "NO_DRAFT_MOCK_SEASON",
]);

const DRAFT_ACTIVE_STATES = new Set(["LOBBY", "DRAFTING", "PAUSED"]);

export const MockDraftControls = ({ leagueId, league, draftState }) => {
  const nav = useNavigate();
  const [stopping, setStopping] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleteError, setDeleteError] = useState(null);

  const leagueType = league?.settings?.league_type;
  const isMock = MOCK_LEAGUE_TYPES.has(leagueType);
  const stateStr = (draftState?.state || draftState?.draft_state || "").toUpperCase();
  const isActive = DRAFT_ACTIVE_STATES.has(stateStr);
  // Stop pauses the engine - only valid mid-draft. From LOBBY the engine
  // raises "Cannot pause from state LOBBY", so don't surface that button.
  const canStop = stateStr === "DRAFTING" || stateStr === "PAUSED";

  if (!isMock || !isActive) return null;

  const handleStop = async () => {
    if (stopping) return;
    setStopping(true);
    try {
      await axios.post(`${API}/commissioner/${leagueId}/pause`, {}, authHeaders());
      nav(`/league/${leagueId}/lobby`);
    } catch (e) {
      // Surface to console - caller flow gracefully degrades on failure
      console.error("Stop draft failed:", e);
      setStopping(false);
    }
  };

  const expectedConfirm = league?.name || "";
  const confirmValid = confirmText.trim() === expectedConfirm && expectedConfirm.length > 0;

  const handleDelete = async () => {
    if (deleting || !confirmValid) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await axios.delete(`${API}/simulator/${leagueId}`, authHeaders());
      nav("/dashboard");
    } catch (e) {
      console.error("Delete league failed:", e);
      const msg = e?.response?.data?.detail || e?.message || "Delete failed - please try again.";
      setDeleteError(msg);
      setDeleting(false);
    }
  };

  return (
    <>
      <div
        className="flex items-center gap-2 px-3 py-2 border-l border-zinc-700"
        data-testid="mock-draft-controls"
      >
        {canStop && (
          <button
            onClick={handleStop}
            disabled={stopping}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded-lg text-amber-400 text-xs font-display font-bold uppercase tracking-wider transition-all disabled:opacity-50"
            data-testid="mock-stop-draft-btn"
            title="Pause this Mock draft and return to lobby"
          >
            <Pause className="w-3.5 h-3.5" />
            {stopping ? "Stopping…" : "Stop Draft"}
          </button>
        )}
        <button
          onClick={() => {
            setShowDeleteModal(true);
            setConfirmText("");
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-xs font-display font-bold uppercase tracking-wider transition-all"
          data-testid="mock-delete-league-btn"
          title="Permanently delete this Mock league"
        >
          <Trash2 className="w-3.5 h-3.5" />
          Delete Mock
        </button>
      </div>

      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="mock-delete-modal"
        >
          <div className="w-full max-w-md bg-zinc-900 border border-red-500/30 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="font-display font-black text-xl text-red-400 uppercase tracking-wider">
                  Delete Mock League
                </h3>
                <p className="text-xs text-zinc-500 mt-1">This action cannot be undone.</p>
              </div>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1.5 hover:bg-zinc-800 rounded-lg transition-colors"
                data-testid="mock-delete-modal-close-btn"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>
            <p className="text-sm text-zinc-300 mb-4">
              This will permanently delete{" "}
              <span className="font-bold text-white">{expectedConfirm}</span> and all of its
              data - draft picks, activity feed, rosters, everything.
            </p>
            <p className="text-xs text-zinc-400 mb-2">
              Type <span className="font-mono text-red-400 font-bold">{expectedConfirm}</span>{" "}
              to confirm:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoFocus
              placeholder="League name"
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-red-500/50 transition-colors mb-4"
              data-testid="mock-delete-confirm-input"
            />
            {deleteError && (
              <div
                className="px-3 py-2 mb-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-xs"
                data-testid="mock-delete-error"
              >
                {deleteError}
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-300 text-sm font-display font-bold uppercase tracking-wider transition-all"
                data-testid="mock-delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={!confirmValid || deleting}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-lg text-red-400 text-sm font-display font-bold uppercase tracking-wider transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="mock-delete-confirm-btn"
              >
                {deleting ? "Deleting…" : "Delete Forever"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MockDraftControls;
